import { Link } from 'react-router-dom'
import { useCoverage, useServices } from '@/api/queries'
import { CoverageMeter } from '@/components/data-truth/CoverageMeter'
import { ProvenanceChip } from '@/components/data-truth/ProvenanceChip'
import { sourceToProvenance } from '@/components/data-truth/provenance'
import { StatusChip } from '@/components/ui/status-chip'
import type { Status } from '@/components/ui/status-chip'

/** Coloring of the real wired÷total ratio against fixed thresholds — not a fabricated criticality signal. */
function severity(ratio: number): Status {
  if (ratio < 0.15) return 'critical'
  if (ratio < 0.3) return 'warn'
  return 'info'
}

/**
 * The FR-CAT-05 fleet data-blindness board — live from /admin/v2/coverage,
 * already ranked worst-first by the store (lowest wired ratio first). Shared
 * between the Fleet tab (full list) and the Home widget (top N).
 */
export function BlindnessBoard({ limit }: { limit?: number }) {
  const { data, isLoading } = useCoverage()
  const { data: servicesData } = useServices()

  const archetypeByKey = new Map((servicesData?.services ?? []).map((s) => [s.key, s.archetype]))

  if (isLoading || !data) {
    return <div className="p-3.5 text-[12.5px] text-ink-2">Loading…</div>
  }

  const rows = limit ? data.coverage.slice(0, limit) : data.coverage

  return (
    <div>
      <div className="flex items-center justify-end px-3.5 py-1.5">
        <ProvenanceChip provenance={sourceToProvenance(data.source, 'ranked worst-first, wired ÷ declared (FR-CAT-05)')} />
      </div>
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            {['#', 'Service', 'Archetype', 'Coverage', 'Caveat', 'Gap'].map((h) => (
              <th
                key={h}
                className="border-b border-border px-2.5 py-1.5 text-left text-[10.5px] font-bold tracking-wide text-ink-2 uppercase"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.service} className="hover:bg-canvas">
              <td className="mono border-b border-border px-2.5 py-2 text-ink-2">{i + 1}</td>
              <td className="border-b border-border px-2.5 py-2">
                <Link to={`/fleet/${row.service}`} className="font-semibold text-accent hover:underline">
                  {row.service}
                </Link>
              </td>
              <td className="border-b border-border px-2.5 py-2 text-[11.5px] text-ink-2">
                {archetypeByKey.get(row.service) ?? <span className="text-unknown">—</span>}
              </td>
              <td className="border-b border-border px-2.5 py-2">
                <span className="flex items-center gap-2">
                  <CoverageMeter wired={row.wired} total={row.total} />
                  <StatusChip status={severity(row.ratio)}>{(row.ratio * 100).toFixed(0)}%</StatusChip>
                </span>
              </td>
              <td className="mono border-b border-border px-2.5 py-2 text-ink-2">{row.caveat}</td>
              <td className="mono border-b border-border px-2.5 py-2 text-ink-2">{row.gap}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
